import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { analytics } from '../services/analytics';
import { r2Storage } from '../services/r2Storage';
import { supabase } from '../lib/supabase';
import {
  trackCheckoutStarted,
  trackFreePlanSelected
} from '../services/ga4Analytics';
import { ArrowRight, CheckCircle } from 'lucide-react';

const OnboardingTrialSetup = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  /* Redirect if already set-up */
  useEffect(() => {
    if (user?.profile?.trial_setup_completed) navigate('/dashboard');
  }, [user, navigate]);

  const handleStartTrial = async () => {
    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Authentication required');
      }

      analytics.track('Checkout Started', {
        plan: 'pro',
        price: 29
      });
      trackCheckoutStarted('pro');

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const res = await fetch(`${apiUrl}/api/stripe/create-trial-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          customerName: user.profile?.full_name,
          // FIXED: Include session_id parameter in success URL
          successUrl: `${window.location.origin}/onboarding/trial-success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/onboarding/trial-setup`
        })
      });
      if (!res.ok) throw new Error('Failed to create checkout session');
      const { sessionId } = await res.json();
      const stripe = await import('@stripe/stripe-js').then(m =>
        m.loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
      );
      await stripe.redirectToCheckout({ sessionId });
    } catch (err) {
      console.error('Stripe checkout error:', err);
      toast.error('Failed to start trial setup.', {
        toastId: 'trial-setup-error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkipTrial = async () => {
    setIsLoading(true);
    try {
      let logoUrl = null;

      // If onboarding stored a pending logo, upload it through the Cloudflare flow now.
      try {
        const pendingLogoData = sessionStorage.getItem('pendingLogoUpload');
        if (pendingLogoData) {
          const logoData = JSON.parse(pendingLogoData);
          const response = await fetch(logoData.fileData);
          const blob = await response.blob();
          const file = new File([blob], logoData.fileName, { type: logoData.fileType });

          const uploadResult = await r2Storage.uploadLogo(file);
          logoUrl = uploadResult.url;
          sessionStorage.removeItem('pendingLogoUpload');
        }
      } catch (logoError) {
        console.warn('Logo upload failed during free setup:', logoError);
        toast.warn('Logo upload failed. You can upload it later in Profile Settings.', {
          toastId: 'free-logo-upload-warning'
        });
      }

      // Mark trial setup as completed with free plan
      const response = await authService.updateProfile({
        subscription_status: 'free',
        trial_setup_completed: true,
        onboarding_completed: true,
        plan_type: 'free',
        free_quotes_limit: 10,
        ...(logoUrl ? { avatar_url: logoUrl } : {})
      });
      
      analytics.track('Free Plan Selected', {
        user_id: user.id,
        plan: 'free',
        logo_uploaded: !!logoUrl
      });
      trackFreePlanSelected();
      
      toast.success('Welcome to QuoteTier Free!', {
        toastId: 'free-plan-success'
      });
      
      navigate('/dashboard');
    } catch (err) {
      console.error('Free plan setup error:', err);
      toast.error('Failed to set up free plan.', {
        toastId: 'free-plan-error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif&family=JetBrains+Mono:wght@400;500&display=swap');
        .font-instrument { font-family: 'Instrument Serif', serif; }
        .font-jetbrains { font-family: 'JetBrains Mono', monospace; }
      `}</style>

      <div className="min-h-screen bg-white font-jetbrains text-black">
        {/* Header */}
        <header className="border-b border-black">
          <div className="max-w-7xl mx-auto px-6 py-5 flex items-center">
            <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="font-instrument text-3xl tracking-tight">QuoteTier</span>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-6 py-16">
          <div className="max-w-2xl mx-auto space-y-12">

            {/* Progress */}
            <div className="flex items-center justify-center space-x-4">
              {['✓', '✓', '3'].map((step, i) => (
                <React.Fragment key={i}>
                  <div
                    className={`w-8 h-8 flex items-center justify-center text-sm font-bold rounded-full ${
                      i < 2 ? 'bg-black text-white' : 'border border-black'
                    }`}
                  >
                    {step}
                  </div>
                  {i < 2 && <div className="w-8 h-px bg-black" />}
                </React.Fragment>
              ))}
            </div>

            {/* Hero */}
            <div className="text-center">
              <h1 className="font-instrument text-4xl sm:text-6xl leading-[0.95] tracking-tight mb-4">
                Choose your <span className="font-instrument font-bold">QuoteTier plan</span>
              </h1>
              <p className="text-xl text-gray-600">
                Start free, upgrade when you're ready to scale
              </p>
            </div>

            {/* Pricing Plans */}
            <div className="grid md:grid-cols-2 gap-6">
              
              {/* Free Plan */}
              <div className="border border-black rounded-2xl p-6 flex flex-col h-full">
                <div className="text-center mb-6">
                  <h3 className="font-instrument text-2xl mb-2">Free</h3>
                  <div className="text-4xl font-bold">$0</div>
                  <div className="text-gray-600">/forever</div>
                </div>
                
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span><strong>10 sent quotes/month</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span><strong>3 e-sign acceptances/month</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Interactive quote builder</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Connect your Gmail/365</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-500">
                    <span className="w-4 h-4 mt-0.5 flex-shrink-0 rounded-full border border-gray-300" />
                    <span className="text-xs">"Powered by QuoteTier" watermark</span>
                  </li>
                </ul>

                {/* Footer CTA */}
                <div className="mt-auto pt-6">
                  <button
                    onClick={handleSkipTrial}
                    className="w-full py-3 border border-black text-black text-lg font-bold rounded-lg hover:bg-black hover:text-white transition flex items-center justify-center gap-2"
                  >
                    Start Free
                    <ArrowRight className="w-5 h-5" />
                  </button>
                  <div className="mt-3 text-xs text-gray-500 text-center">
                    Upgrade anytime
                  </div>
                </div>
              </div>
              
              {/* Pro Plan */}
              <div className="border-2 border-black rounded-2xl p-6 relative flex flex-col h-full">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-black text-white px-4 py-1 text-sm font-bold rounded-full">
                  MOST POPULAR
                </div>
                
                <div className="text-center mb-6 pt-5">
                  <h3 className="font-instrument text-2xl mb-2">Pro</h3>
                  <div className="flex items-center justify-center space-x-2">
                    <div className="text-2xl text-gray-400 line-through">$49</div>
                    <div className="text-4xl font-bold text-green-600">$29</div>
                  </div>
                  <div className="text-gray-600">/month</div>
                  <div className="text-xs bg-green-50 border border-green-200 text-green-800 px-3 py-1 rounded-full inline-block mt-2">
                    Founders price (limited time)
                  </div>
                </div>
                
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span><strong>Unlimited sent quotes</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span><strong>Unlimited e-sign acceptances</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Remove watermark</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Custom branding + logo</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Saved templates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Analytics & tracking</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Managed SMTP + deliverability</span>
                  </li>
                </ul>

                {/* Footer CTA */}
                <div className="mt-auto pt-6">
                  <button
                    onClick={handleStartTrial}
                    disabled={isLoading}
                    className="w-full py-3 bg-black text-white text-lg font-bold rounded-lg hover:bg-gray-800 disabled:opacity-50 transition flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Setting up...
                      </>
                    ) : (
                      <>
                        Start Free — 3 Quotes/Mo
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>

                  <div className="mt-3 text-xs text-gray-500 text-center">
                    Secure checkout • Cancel anytime
                  </div>
                </div>
              </div>
              
            </div>

            {/* FAQ */}
            <div className="border border-black rounded-2xl p-6">
              <h3 className="font-instrument text-2xl text-center mb-4">Questions?</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="font-semibold">What's included in free?</p>
                  <p className="text-gray-600">10 sent quotes + 3 e-sign acceptances per month. Includes small "Powered by QuoteTier" watermark.</p>
                </div>
                <div>
                  <p className="font-semibold">How long is the Founders price?</p>
                  <p className="text-gray-600">$29/month pricing locked for 90 days, then $49/month. Early users keep $29 forever.</p>
                </div>
                <div>
                  <p className="font-semibold">What happens when I hit my limit?</p>
                  <p className="text-gray-600">Free: Upgrade prompt. Pro: Unlimited everything. Cancel anytime.</p>
                </div>
              </div>
            </div>

          </div>
        </main>
      </div>
    </>
  );
};

export default OnboardingTrialSetup;
